import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

import { Input, Checkbox, Icon } from '../../';
import { keyCode, formatData } from '../../../helpers';
import { useTooltip } from '../../../hooks';

export const ChangelogToolbar = ({
  itemTotalCount,
  itemCount,
  requestFilter,
  filterTerm,
  filterObjects,
  onFilterObjectsFilter,
  showSchemaChangesOnlyOption,
  onSchemaChange,
  schemaChangesOnly,
  latestSyncJobs = [],
  nextSyncJobs = [],
}) => {
  const inputRef = useRef();
  const { showTooltip, hideTooltip } = useTooltip();

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleKeyDown = e => {
    if ((e.ctrlKey || e.metaKey) && e.keyCode === keyCode.F) {
      e.preventDefault();
      inputRef.current.focus();
    }

    if (e.keyCode === keyCode.ESCAPE) {
      requestFilter('');
      setTimeout(() => inputRef.current.blur(), 100);
    }
  };
  const handleInputChange = event => {
    let { value } = '';

    // Handle standard form inputs
    if (event.target !== undefined) {
      value = event.target.value;

      // Handle custom form inputs
    } else {
      value = event.value;
    }

    if (value === ' ' && filterTerm === '') {
      // do nothing
    } else {
      requestFilter(value);
    }
  };

  return (
    <div className="DataTableToolbar main-toolbar">
      {latestSyncJobs && latestSyncJobs.length > 0 && (
        <div className="main-toolbar-item sync-info">
          <Icon name="reload" tooltip="This asset is automatically synced with its datasource" />
          {latestSyncJobs.length === 1 ? (
            <Link
              to={`/tasks/${latestSyncJobs[0].task_id}/${latestSyncJobs[0].job_id}`}
              onMouseEnter={() =>
                showTooltip({ title: 'Go to the latest datasource sync job for this asset' })
              }
              onMouseLeave={hideTooltip}
            >
              <span className="sync-info-label">Last synced</span>
              {formatData(latestSyncJobs[0].job_completed_timestamp, 'datetime')}
            </Link>
          ) : (
            <div className="sync-info-dropdown">
              <Link to={`/tasks/${latestSyncJobs[0].task_id}/${latestSyncJobs[0].job_id}`}>
                <span className="sync-info-label">
                  Last synced ({latestSyncJobs[0].connector_name})
                </span>
                {formatData(latestSyncJobs[0].job_completed_timestamp, 'datetime')}
              </Link>
              <div className="sync-info-dropdown__menu">
                {latestSyncJobs.map(job => (
                  <Link
                    key={job.job_id}
                    className="sync-info-dropdown__option"
                    to={`/tasks/${job.task_id}/${job.job_id}`}
                    onMouseEnter={() =>
                      showTooltip({
                        title: `Go to the latest ${job.connector_name} datasource sync job for this asset`,
                      })
                    }
                    onMouseLeave={hideTooltip}
                  >
                    <span className="sync-info-label">{job.connector_name}</span>
                    {formatData(job.job_completed_timestamp, 'datetime')}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {nextSyncJobs && nextSyncJobs.length > 0 && (
        <div className="main-toolbar-item sync-info">
          {nextSyncJobs.length === 1 ? (
            <Link
              to={`/tasks/${nextSyncJobs[0].task_id}`}
              onMouseEnter={() =>
                showTooltip({ title: 'Go to the task scheduled to sync this asset' })
              }
              onMouseLeave={hideTooltip}
            >
              <span className="sync-info-label">Next scheduled sync</span>
              {formatData(nextSyncJobs[0].next_run_timestamp, 'datetime')}
            </Link>
          ) : (
            <div className="sync-info-dropdown">
              <Link to={`/tasks/${nextSyncJobs[0].task_id}`}>
                <span className="sync-info-label">
                  Next scheduled sync ({nextSyncJobs[0].connector_name})
                </span>
                {formatData(nextSyncJobs[0].next_run_timestamp, 'datetime')}
              </Link>
              <div className="sync-info-dropdown__menu">
                {nextSyncJobs.map(task => (
                  <Link
                    key={task.task_id}
                    className="sync-info-dropdown__option"
                    to={`/tasks/${task.task_id}`}
                    onMouseEnter={() =>
                      showTooltip({
                        title: `Go to the ${task.connector_name} task scheduled to sync this asset`,
                      })
                    }
                    onMouseLeave={hideTooltip}
                  >
                    <span className="sync-info-label">{task.connector_name}</span>
                    {formatData(task.next_run_timestamp, 'datetime')}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      <div
        className="filter-container"
        onMouseEnter={() => showTooltip({ title: 'Type to filter changelog' })}
        onMouseLeave={hideTooltip}
      >
        <form autoComplete="off">
          {filterTerm && (
            <div className="main-toolbar-text">{`Showing ${itemCount} of ${itemTotalCount} events`}</div>
          )}
          {showSchemaChangesOnlyOption && (
            <Checkbox
              name="schema_changes_only"
              label="Schema Changes Only"
              title="Show schema changes only"
              checked={schemaChangesOnly}
              onChange={onSchemaChange}
            />
          )}
          <Input
            ref={inputRef}
            type="text"
            name="changelog_filter"
            value={filterTerm}
            onChange={handleInputChange}
            placeholder="Filter..."
            isNullable={true}
            icon="filter"
            iconAsButton={true}
            withSelect={true}
            selectProps={[
              {
                name: 'filter_object',
                value: filterObjects.filter(x => x.checked),
                label: false,
                tooltip: 'Select columns to filter on',
                options: filterObjects,
                onChange: onFilterObjectsFilter,
              },
            ]}
          />
        </form>
      </div>
    </div>
  );
};
